import type { GetUserByIdQuery } from '../../__generated__/graphql/api';

type GeneratedUser = NonNullable<NonNullable<NonNullable<GetUserByIdQuery['listUsers']>['users']>[number]>;

type GeneratedAddress = NonNullable<GeneratedUser['billingAddress']>;

type GeneratedLoan = NonNullable<GeneratedUser['loans']>[number];

type GeneratedCard = NonNullable<NonNullable<GeneratedUser['cards']>[number]>;

export interface UserAddressView {
  id: string | null;
  addressFirstLine: string | null;
  addressSecondLine: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  isPoBox: boolean | null;
}

export interface UserCardListItem {
  id: string;
  status: string | null;
  referenceId: string | null;
  active: boolean | null;
  lastFour: string | null;
  expDate: string | null;
  physicalActivatedAt: string | null;
  blockedReason: string | null;
  dateOpened: string | null;
  insertedAt: string | null;
}

export interface UserLoanListItem {
  id: string;
  partnerAccountNumber: string | null;
  status: string | null;
  isDelinquent: boolean | null;
  programName: string | null;
}

export interface UserDetailViewModel {
  id: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  dob: string | null;
  isFriendsAndFam: boolean | null;
  insertedAt: string | null;

  billingAddress: UserAddressView | null;
  shippingAddress: UserAddressView | null;
  loans: UserLoanListItem[];
  cards: UserCardListItem[];
}

const toAddress = (address: GeneratedAddress | null | undefined): UserAddressView | null => {
  if (!address) return null;

  return {
    id: address.id ?? null,
    addressFirstLine: address.addressFirstLine ?? null,
    addressSecondLine: address.addressSecondLine ?? null,
    city: address.city ?? null,
    state: address.state ?? null,
    zip: address.zip ?? null,
    isPoBox: address.isPoBox ?? null,
  };
};

const toUserCardListItem = (card: GeneratedCard): UserCardListItem => ({
  id: card.id ?? '',
  status: card.status ?? null,
  referenceId: card.referenceId ?? null,
  active: card.active ?? null,
  lastFour: card.lastFour ?? null,
  expDate: card.expDate ?? null,
  physicalActivatedAt: card.physicalActivatedAt ?? null,
  blockedReason: card.blockedReason ?? null,
  dateOpened: card.dateOpened ?? null,
  insertedAt: card.insertedAt ?? null,
});

const toUserLoanListItem = (loan: GeneratedLoan): UserLoanListItem => ({
  id: loan.id ?? '',
  partnerAccountNumber: loan.partnerAccountNumber ?? null,
  status: loan.status ?? null,
  isDelinquent: loan.isDelinquent ?? null,
  programName: loan.program?.name ?? null,
});

export const toUserDetail = (user: GeneratedUser): UserDetailViewModel => ({
  id: user.id ?? '',
  firstName: user.firstName ?? null,
  middleName: user.middleName ?? null,
  lastName: user.lastName ?? null,
  email: user.email ?? null,
  phone: user.phone ?? null,
  dob: user.dob ?? null,
  isFriendsAndFam: user.isFriendsAndFam ?? null,
  insertedAt: user.insertedAt ?? null,

  billingAddress: toAddress(user.billingAddress),
  shippingAddress: toAddress(user.shippingAddress),
  loans: (user.loans ?? []).filter((loan): loan is GeneratedLoan => Boolean(loan)).map(toUserLoanListItem),
  cards: (user.cards ?? []).filter((card): card is GeneratedCard => Boolean(card)).map(toUserCardListItem),
});
